import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from '@components/common/Modal/Modal';
import Button from '@components/Button/Button';
import evaluationsService, {
    type ImportReport,
    type ImportRowResult,
} from '@/services/evaluations/evaluationsService';
import JudgeSelect from './JudgeSelect';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onImported: () => void;
    /** When set, the server also rejects rows whose student is not in this cohort. */
    cohortId?: string;
}

const rowPill = (row: ImportRowResult, label: (k: string) => string) => {
    const map = {
        ok: 'bg-green-50 text-green-700',
        warning: 'bg-amber-50 text-amber-700',
        error: 'bg-red-50 text-red-700',
    } as const;
    return (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[row.status]}`}>
            {row.outcome ? label(`outcome${row.outcome[0].toUpperCase()}${row.outcome.slice(1)}`) : row.status}
        </span>
    );
};

const ImportEvaluationModal: React.FC<Props> = ({ isOpen, onClose, onImported, cohortId }) => {
    const { t } = useTranslation('evaluation');
    const fileRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [report, setReport] = useState<ImportReport | null>(null);
    const [busy, setBusy] = useState<'validate' | 'commit' | null>(null);

    const [overwrite, setOverwrite] = useState(true);
    const [submit, setSubmit] = useState(true);
    const [allowPartial, setAllowPartial] = useState(false);

    // Auto-filled from the file's embedded judge once validated, but the admin
    // can override it — the file's judge is a default, not a lock.
    const [judgeOverride, setJudgeOverride] = useState('');

    const reset = () => {
        setFile(null);
        setReport(null);
        setBusy(null);
        setAllowPartial(false);
        setJudgeOverride('');
        if (fileRef.current) fileRef.current.value = '';
    };

    const close = () => {
        reset();
        onClose();
    };

    const runValidate = async (f: File, judgeId?: string) => {
        setBusy('validate');
        setReport(null);
        try {
            const r = await evaluationsService.importEvaluations(f, {
                mode: 'validate',
                submit,
                cohort_id: cohortId,
                judge_id: judgeId || undefined,
            });
            setReport(r);
            // Auto-fill the picker from whatever judge the file/override resolved to,
            // so the admin sees (and can correct) who these scores are being saved as.
            if (r.summary.judge_id) setJudgeOverride(r.summary.judge_id);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || t('import.failed'));
        } finally {
            setBusy(null);
        }
    };

    const onPick = (f: File | undefined) => {
        if (!f) return;
        setFile(f);
        setJudgeOverride('');
        runValidate(f);
    };

    const handleJudgeOverrideChange = (id: string) => {
        setJudgeOverride(id);
        if (file) runValidate(file, id);
    };

    const runCommit = async () => {
        if (!file) return;
        setBusy('commit');
        try {
            const r = await evaluationsService.importEvaluations(file, {
                mode: 'commit',
                on_conflict: overwrite ? 'overwrite' : 'skip',
                submit,
                allow_partial: allowPartial,
                cohort_id: cohortId,
                judge_id: judgeOverride || undefined,
            });
            setReport(r);
            if (r.summary.committed) {
                toast.success(
                    t('import.done', { imported: r.summary.imported ?? 0, skipped: r.summary.skipped ?? 0 }),
                );
                onImported();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || t('import.failed'));
        } finally {
            setBusy(null);
        }
    };

    if (!isOpen) return null;

    const s = report?.summary;
    const hasErrors = (s?.error ?? 0) > 0;
    const committable = allowPartial ? (s?.ok ?? 0) + (s?.warning ?? 0) : (s?.ok ?? 0) + (s?.warning ?? 0);
    const canCommit = !!report && !s?.committed && (!hasErrors || allowPartial) && committable > 0;

    return (
        <Modal isOpen onClose={close} title={t('import.title')} maxWidth="max-w-[820px]">
            <div className="flex flex-col max-h-[calc(90vh-52px)]">
                <div className="px-5 py-4 border-b border-gray-100 shrink-0">
                    <p className="text-xs text-gray-500 mb-3">{t('import.pickHint')}</p>
                    <div className="flex items-center gap-3">
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="hidden"
                            onChange={(e) => onPick(e.target.files?.[0])}
                        />
                        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!!busy}>
                            {t('import.choose')}
                        </Button>
                        <span className="text-xs text-gray-600 truncate">
                            {file ? file.name : t('import.pick')}
                        </span>
                        {busy === 'validate' && <span className="text-xs text-gray-400">{t('import.checking')}</span>}
                        {file && !busy && (
                            <button
                                type="button"
                                className="text-xs font-semibold text-[var(--primary-color)] hover:underline"
                                onClick={() => runValidate(file)}
                            >
                                {t('import.reCheck')}
                            </button>
                        )}
                    </div>
                </div>

                {s && (
                    <>
                        <div className="px-5 py-3 border-b border-gray-100 shrink-0 space-y-2">
                            <div className="text-xs font-semibold text-gray-700">
                                {t('import.summary', { ok: s.ok, warning: s.warning, error: s.error, total: s.total })}
                            </div>

                            {s.file_warnings?.length > 0 && (
                                <ul className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1 list-disc list-inside">
                                    {s.file_warnings.map((w, i) => (
                                        <li key={i}>{w}</li>
                                    ))}
                                </ul>
                            )}

                            {!s.committed && (
                                <div className="pt-1 max-w-xs">
                                    <JudgeSelect
                                        value={judgeOverride}
                                        onChange={handleJudgeOverrideChange}
                                        isDisabled={busy === 'validate'}
                                        required
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">{t('import.judgeHint')}</p>
                                </div>
                            )}

                            {!s.committed && (
                                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-600 pt-1">
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                                        {t('import.optOverwrite')}
                                    </label>
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={submit}
                                            onChange={(e) => {
                                                setSubmit(e.target.checked);
                                                if (file) runValidate(file);
                                            }}
                                        />
                                        {t('import.optSubmit')}
                                    </label>
                                    {hasErrors && (
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={allowPartial}
                                                onChange={(e) => setAllowPartial(e.target.checked)}
                                            />
                                            {t('import.optAllowPartial')}
                                        </label>
                                    )}
                                </div>
                            )}

                            {!s.committed && hasErrors && !allowPartial && (
                                <div className="text-[11px] text-red-700">{t('import.blocked')}</div>
                            )}
                        </div>

                        <div className="overflow-y-auto">
                            <table className="min-w-full text-left text-xs">
                                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 font-semibold text-gray-600">{t('import.colRow')}</th>
                                        <th className="px-4 py-2 font-semibold text-gray-600">{t('import.colStudent')}</th>
                                        <th className="px-4 py-2 font-semibold text-gray-600">{t('import.colStatus')}</th>
                                        <th className="px-4 py-2 font-semibold text-gray-600">{t('import.colDetail')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {report!.rows.map((row) => (
                                        <tr key={row.row_number} className="align-top">
                                            <td className="px-4 py-2 text-gray-400">{row.row_number}</td>
                                            <td className="px-4 py-2 text-gray-700">
                                                {row.student_name || row.enrollment_number || '—'}
                                                {row.enrollment_number && (
                                                    <div className="text-[10px] text-gray-400">{row.enrollment_number}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2">{rowPill(row, (k) => t(`import.${k}`))}</td>
                                            <td className="px-4 py-2 text-gray-600">
                                                {row.errors.map((e, i) => (
                                                    <div key={`e${i}`} className="text-red-600">
                                                        {e.message}
                                                    </div>
                                                ))}
                                                {row.warnings.map((w, i) => (
                                                    <div key={`w${i}`} className="text-amber-600">
                                                        {w}
                                                    </div>
                                                ))}
                                                {row.errors.length === 0 && row.preview && (
                                                    <span className="text-gray-500">
                                                        {row.preview.total_weighted} / 100{' '}
                                                        {row.preview.target_status === 'SUBMITTED'
                                                            ? t('import.targetSubmitted')
                                                            : t('import.targetDraft')}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2 shrink-0">
                    <Button variant="outline" onClick={close} disabled={busy === 'commit'}>
                        {s?.committed ? t('import.close') : t('import.cancel')}
                    </Button>
                    {report && !s?.committed && (
                        <Button
                            variant="primary"
                            onClick={runCommit}
                            isLoading={busy === 'commit'}
                            disabled={!canCommit || !!busy}
                        >
                            {busy === 'commit'
                                ? t('import.importing')
                                : allowPartial && hasErrors
                                    ? t('import.commitPartial', { n: committable })
                                    : t('import.commit', { n: committable })}
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ImportEvaluationModal;
